import React, { useState, useEffect } from 'react';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CalendarClock, CheckCircle2, XCircle, Clock, Trash2, Mail, Users } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  useReportSchedule,
  calcularProximo,
  DIAS_SEMANA,
  type ScheduleFrequencia,
  type SchedulePayload,
} from '@/hooks/useReportSchedule';

interface Props {
  contratoId: string;
  templateId: string | null;   // null = template ainda não salvo
}

const PERFIS_OPCOES = [
  { value: 'admin', label: '🔑 Admin' },
  { value: 'coadmin', label: '🔑 Co-Admin' },
  { value: 'gestor', label: '📋 Gestor' },
  { value: 'fiscal', label: '👷 Fiscal' },
];

export const ScheduleTab: React.FC<Props> = ({ contratoId, templateId }) => {
  const { schedule, isLoading, upsertSchedule, toggleAtivo, deleteSchedule } =
    useReportSchedule(contratoId, templateId);

  // ── Form state ────────────────────────────────────────────────────────────
  const [frequencia, setFrequencia] = useState<ScheduleFrequencia>('semanal');
  const [hora, setHora] = useState('08:00');
  const [diaSemana, setDiaSemana] = useState<number>(1);
  const [diaMes, setDiaMes] = useState<number>(1);
  const [emailsInput, setEmailsInput] = useState(''); // chip input
  const [emails, setEmails] = useState<string[]>([]);
  const [perfis, setPerfis] = useState<string[]>([]);
  const [dirty, setDirty] = useState(false);

  // Preencher form se schedule existente
  useEffect(() => {
    if (schedule) {
      setFrequencia(schedule.frequencia);
      setHora(schedule.hora_execucao.slice(0, 5));
      if (schedule.dia_semana != null) setDiaSemana(schedule.dia_semana);
      if (schedule.dia_mes != null) setDiaMes(schedule.dia_mes);
      setEmails(schedule.destinatarios_emails);
      setPerfis(schedule.destinatarios_perfis);
      setDirty(false);
    }
  }, [schedule]);

  const markDirty = () => setDirty(true);

  // ── E-mail chip ──────────────────────────────────────────────────────────
  const addEmail = () => {
    const raw = emailsInput.trim().toLowerCase();
    if (!raw || emails.includes(raw)) { setEmailsInput(''); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw)) return;
    setEmails(e => [...e, raw]);
    setEmailsInput('');
    markDirty();
  };

  const removeEmail = (e: string) => { setEmails(arr => arr.filter(x => x !== e)); markDirty(); };

  const togglePerfil = (p: string) => {
    setPerfis(arr => arr.includes(p) ? arr.filter(x => x !== p) : [...arr, p]);
    markDirty();
  };

  // ── Preview próxima execução ──────────────────────────────────────────────
  const payload: SchedulePayload = {
    frequencia,
    hora_execucao: hora + ':00',
    dia_semana: frequencia === 'semanal' ? diaSemana : null,
    dia_mes: frequencia === 'mensal' ? diaMes : null,
    destinatarios_emails: emails,
    destinatarios_perfis: perfis,
  };
  const proximaPreview = calcularProximo(payload);

  const handleSalvar = () => {
    upsertSchedule.mutate(payload, { onSuccess: () => setDirty(false) });
  };

  // ── Guards ───────────────────────────────────────────────────────────────
  if (!templateId) {
    return (
      <div className="flex flex-col items-center justify-center py-10 gap-3 text-center">
        <CalendarClock className="h-8 w-8 text-muted-foreground/50" />
        <p className="text-sm text-muted-foreground">
          Salve o relatório primeiro para ativar o agendamento.
        </p>
      </div>
    );
  }

  if (isLoading) {
    return <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-10 w-full" />)}</div>;
  }

  // ── Status badge do último envio ─────────────────────────────────────────
  const StatusBadge = () => {
    if (!schedule) return null;
    const s = schedule.status_ultimo;
    if (s === 'ok') return <Badge variant="default" className="gap-1"><CheckCircle2 className="h-3 w-3" /> Enviado</Badge>;
    if (s === 'falha') return <Badge variant="destructive" className="gap-1"><XCircle className="h-3 w-3" /> Falha</Badge>;
    if (s === 'pendente') return <Badge variant="secondary" className="gap-1"><Clock className="h-3 w-3" /> Pendente</Badge>;
    return <Badge variant="outline">Nunca executado</Badge>;
  };

  return (
    <div className="space-y-5">
      {/* ── Status atual ─────────────────────────────────── */}
      {schedule && (
        <div className="rounded-lg border bg-muted/30 px-4 py-3 space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Switch
                checked={schedule.ativo}
                onCheckedChange={(v) => toggleAtivo.mutate(v)}
                disabled={toggleAtivo.isPending}
              />
              <span className="text-sm font-medium">
                {schedule.ativo ? 'Agendamento ativo' : 'Agendamento suspenso'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <StatusBadge />
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-muted-foreground hover:text-destructive"
                onClick={() => deleteSchedule.mutate()}
                disabled={deleteSchedule.isPending}
                title="Remover agendamento"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>

          {schedule.ultimo_executado_em && (
            <p className="text-xs text-muted-foreground">
              Último envio: {format(new Date(schedule.ultimo_executado_em), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
              {schedule.erro_ultimo && (
                <span className="text-destructive ml-2">— {schedule.erro_ultimo.slice(0, 80)}</span>
              )}
            </p>
          )}
          {schedule.proximo_em && schedule.ativo && (
            <p className="text-xs text-muted-foreground">
              Próximo envio: {format(new Date(schedule.proximo_em), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
            </p>
          )}
        </div>
      )}

      {/* ── Frequência ──────────────────────────────────────── */}
      <div className="space-y-2">
        <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          Frequência
        </Label>
        <Select value={frequencia} onValueChange={(v) => { setFrequencia(v as ScheduleFrequencia); markDirty(); }}>
          <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="diaria">Diária</SelectItem>
            <SelectItem value="semanal">Semanal</SelectItem>
            <SelectItem value="mensal">Mensal</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* ── Dia da semana (semanal) ──────────────────────── */}
      {frequencia === 'semanal' && (
        <div className="space-y-2">
          <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            Dia da semana
          </Label>
          <Select value={String(diaSemana)} onValueChange={(v) => { setDiaSemana(Number(v)); markDirty(); }}>
            <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              {DIAS_SEMANA.map(d => <SelectItem key={d.value} value={String(d.value)}>{d.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* ── Dia do mês (mensal) ──────────────────────────── */}
      {frequencia === 'mensal' && (
        <div className="space-y-2">
          <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            Dia do mês (1–28)
          </Label>
          <Input
            type="number"
            min={1} max={28}
            value={diaMes}
            onChange={(e) => { setDiaMes(Math.min(28, Math.max(1, Number(e.target.value)))); markDirty(); }}
            className="h-9 w-24"
          />
        </div>
      )}

      {/* ── Horário ─────────────────────────────────────────── */}
      <div className="space-y-2">
        <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          Horário (Brasília)
        </Label>
        <Input
          type="time"
          value={hora}
          onChange={(e) => { setHora(e.target.value); markDirty(); }}
          className="h-9 w-32"
        />
      </div>

      <Separator />

      {/* ── Destinatários — Perfis ───────────────────────── */}
      <div className="space-y-2">
        <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground flex items-center gap-1">
          <Users className="h-3.5 w-3.5" /> Perfis do contrato
        </Label>
        <div className="flex flex-wrap gap-1.5">
          {PERFIS_OPCOES.map(p => (
            <button
              key={p.value}
              type="button"
              onClick={() => togglePerfil(p.value)}
              className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                perfis.includes(p.value)
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-muted text-muted-foreground border-border hover:border-primary/50'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Destinatários — E-mails ──────────────────────── */}
      <div className="space-y-2">
        <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground flex items-center gap-1">
          <Mail className="h-3.5 w-3.5" /> E-mails adicionais
        </Label>
        <div className="flex gap-2">
          <Input
            type="email"
            value={emailsInput}
            onChange={(e) => setEmailsInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addEmail(); } }}
            placeholder="email@exemplo.com"
            className="h-9 flex-1 text-sm"
          />
          <Button variant="outline" size="sm" onClick={addEmail} className="shrink-0">
            Adicionar
          </Button>
        </div>
        {emails.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            {emails.map(e => (
              <span
                key={e}
                className="text-xs bg-muted border border-border rounded-full px-2.5 py-0.5 flex items-center gap-1.5"
              >
                {e}
                <button
                  type="button"
                  onClick={() => removeEmail(e)}
                  className="text-muted-foreground hover:text-destructive"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      <Separator />

      {/* ── Preview próxima execução ─────────────────────── */}
      <div className="rounded-lg bg-muted/30 border border-border/50 px-3 py-2.5 text-xs text-muted-foreground flex items-center gap-2">
        <CalendarClock className="h-3.5 w-3.5 shrink-0" />
        <span>
          Próximo envio estimado:{' '}
          <strong className="text-foreground">
            {format(proximaPreview, "EEEE, dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
          </strong>
        </span>
      </div>

      {/* ── Salvar ──────────────────────────────────────────── */}
      <Button
        onClick={handleSalvar}
        disabled={upsertSchedule.isPending || (!dirty && !!schedule)}
        className="w-full gap-2"
      >
        <CalendarClock className="h-4 w-4" />
        {schedule ? (dirty ? 'Salvar alterações' : 'Agendamento salvo') : 'Ativar agendamento'}
      </Button>
    </div>
  );
};
